/**
 * ImageUploader — input de archivo + cropper completo con herramientas de
 * edición pensadas para usuarios sin Photoshop.
 *
 * Flow:
 *   1. Click → file picker (image/jpeg|png|webp)
 *   2. Si la foto pesa mucho la pre-comprimimos antes de mostrarla en el
 *      cropper (los celulares actuales sacan 8-15 MB; cargar eso directo
 *      en el cropper lagueaba todo)
 *   3. Modal con react-easy-crop + controles: slider de zoom, botones
 *      rotar ±90°, "Imagen completa" (reset zoom + centro)
 *   4. Aplicar → blob recortado → uploadImage(blob) → onChange(url)
 *
 * Cambios respecto a la v1:
 *   - `objectFit="contain"` + `minZoom=0.5` permite alejar más allá del
 *     borde para fotos muy grandes / con poca área útil
 *   - Pre-compresión automática a 2000px (lado mayor) cuando el archivo
 *     pesa más que el límite — antes rechazábamos directo "muy grande"
 *   - Límite bumpeado de 5MB a 20MB porque hoy las fotos del celular
 *     pasan los 5MB tranquilo
 *   - Rotación con botones ±90° (afecta el blob final)
 *   - Botones de preset: "Imagen completa" (zoom=1, center) y "Llenar"
 *     (zoom=auto a cover)
 *   - Indicador de zoom %, slider más cómodo
 *   - Doble click sobre la imagen = reset al "imagen completa"
 */
import { useCallback, useRef, useState, type ChangeEvent } from "react";
import Cropper, { type Area } from "react-easy-crop";
import { Icon } from "./Icon";
import { useToast } from "./toast";
import { uploadImage } from "../api/uploads";
import { ApiError } from "../api/client";

export interface ImageUploaderProps {
  /** URL actual (puede ser http(s) remota o data: temporal). null = vacío. */
  value: string | null;
  /** Llamado con la URL absoluta tras subir, o con null si el usuario quita la imagen. */
  onChange: (url: string | null) => void;
  /** Carpeta destino en el backend. */
  folder: "brand" | "client";
  /** Recorte visual del preview. Default "square". */
  shape?: "circle" | "square";
  /** Tamaño del preview en px (lado). Default 120. */
  size?: number;
  /** Aspect ratio del crop. Default 1 (cuadrado). */
  aspect?: number;
  /** Texto del placeholder cuando no hay imagen. */
  label?: string;
}

const ACCEPT = "image/jpeg,image/png,image/webp";
/** Limit que viaja al backend (post-compresión). El backend tiene su propio
 *  cap; mantenelos sincronizados. */
const MAX_UPLOAD_BYTES = 8 * 1024 * 1024;          // 8 MB tras compresión
/** Limit del input directo. Si excede, comprimimos antes de meter al cropper. */
const MAX_INPUT_BYTES = 20 * 1024 * 1024;          // 20 MB de archivo crudo
/** Lado máximo del source que carga el cropper — si la foto es mayor, la
 *  reducimos previamente para que el cropper no laguee. */
const MAX_SOURCE_SIDE = 2400;
/** Lado máximo del blob final que sube al backend (1024px = foto perfil
 *  bien definida sin ser exagerada). */
const MAX_OUTPUT_SIDE = 1024;

/** Presets de proporción de salida. "natural" = usa el aspect natural de la
 *  foto (sin recortar absolutamente nada). Los otros son los más comunes:
 *  cuadrada para perfil/redes, 4:5 vertical para portadas tipo Instagram,
 *  4:3 horizontal para galerías clásicas. */
const ASPECT_PRESETS: Array<{ key: string; label: string; ratio: number | "natural" }> = [
  { key: "natural",  label: "Foto completa",  ratio: "natural" },
  { key: "square",   label: "Cuadrada 1:1",    ratio: 1 },
  { key: "portrait", label: "Vertical 4:5",    ratio: 4 / 5 },
  { key: "landscape", label: "Horizontal 4:3", ratio: 4 / 3 },
];

export function ImageUploader({
  value,
  onChange,
  folder,
  shape = "square",
  size = 120,
  aspect: defaultAspect = 1,
  label = "Subir foto",
}: ImageUploaderProps) {
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [srcDataUrl, setSrcDataUrl] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedArea, setCroppedArea] = useState<Area | null>(null);
  const [uploading, setUploading] = useState(false);
  const [preparing, setPreparing] = useState(false);
  /** Aspect activo del cropper. Arranca en el default que pasa el padre
   *  (típicamente 1:1) pero el usuario puede cambiarlo o pedir "foto completa"
   *  (que se resuelve al ratio natural de la imagen cargada). */
  const [aspect, setAspect] = useState<number>(defaultAspect);
  /** Dimensiones naturales de la imagen — necesario para resolver "Foto
   *  completa" al aspect real. Se setean cuando se carga el archivo. */
  const [naturalDims, setNaturalDims] = useState<{ width: number; height: number } | null>(null);
  /** Mime original del archivo. Si es PNG/WebP, el blob final también va a
   *  ser PNG para preservar transparencia (logos del coach). JPG → JPG. */
  const [sourceMime, setSourceMime] = useState<string>("image/jpeg");

  const openPicker = () => fileInputRef.current?.click();

  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // reset para permitir re-seleccionar el mismo file
    if (!f) return;
    if (!ACCEPT.split(",").includes(f.type)) {
      toast(`Tipo no permitido: ${f.type || "desconocido"}. Usá JPEG, PNG o WebP.`);
      return;
    }
    if (f.size > MAX_INPUT_BYTES) {
      toast(`Archivo muy grande (${(f.size / 1024 / 1024).toFixed(1)} MB). Máx ${(MAX_INPUT_BYTES / 1024 / 1024)} MB.`);
      return;
    }

    setPreparing(true);
    try {
      // Pre-comprimimos el source si es muy grande para que el cropper no laguee.
      // Para fotos chicas pasamos el dataURL directo sin tocar pixels.
      const raw = await readAsDataURL(f);
      const prepared = f.size > 2 * 1024 * 1024
        ? await downscaleDataUrl(raw, MAX_SOURCE_SIDE, f.type)
        : raw;
      // Detectamos las dimensiones naturales para soportar el preset
      // "Foto completa" (que copia el aspect real de la imagen → cero recorte).
      const dims = await readImageDims(prepared);
      setNaturalDims(dims);
      setSrcDataUrl(prepared);
      // Trackeamos el mime original para preservar transparencia: PNG/WebP
      // se vuelven a serializar como PNG (mantiene fondo transparente del
      // logo del coach). JPG queda como JPG (pesa menos, mejor para fotos).
      setSourceMime(f.type);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setRotation(0);
      setCroppedArea(null);
      // Reseteamos el aspect al default del padre — si el padre quería 1:1
      // (foto de perfil), volvemos a 1:1 al abrir otra imagen.
      setAspect(defaultAspect);
    } catch (err) {
      toast("No pude leer la imagen: " + (err instanceof Error ? err.message : ""));
    } finally {
      setPreparing(false);
    }
  };

  /** Cambia el aspect del cropper. "natural" usa la proporción real de la
   *  imagen cargada — resultado SIN recorte alguno. Útil para coachs que
   *  quieren mantener la foto tal cual la sacaron. */
  const applyAspectPreset = (ratio: number | "natural") => {
    if (ratio === "natural") {
      if (!naturalDims) return;
      const r = naturalDims.width / naturalDims.height;
      setAspect(r);
    } else {
      setAspect(ratio);
    }
    // Al cambiar de proporción, reseteamos zoom/crop para mostrar la nueva
    // selección centrada — evita que el usuario quede con el crop en una
    // esquina rara cuando cambia 1:1 → 4:5.
    setZoom(1);
    setCrop({ x: 0, y: 0 });
  };

  const onCropComplete = useCallback((_area: Area, areaPixels: Area) => {
    setCroppedArea(areaPixels);
  }, []);

  const closeCropper = () => {
    setSrcDataUrl(null);
    setCroppedArea(null);
    setZoom(1);
    setRotation(0);
    setCrop({ x: 0, y: 0 });
  };

  const resetView = () => {
    setZoom(1);
    setCrop({ x: 0, y: 0 });
  };

  const rotateLeft = () => setRotation((r) => (r - 90 + 360) % 360);
  const rotateRight = () => setRotation((r) => (r + 90) % 360);

  const applyCrop = async () => {
    if (!srcDataUrl) return;
    setUploading(true);
    try {
      // Si el cropper todavía no disparó onCropComplete (el usuario clickeó
      // Aplicar muy rápido) caemos a "crop = imagen entera" cargando el size
      // natural — así nunca el botón queda inutilizable.
      let area = croppedArea;
      if (!area) {
        const dims = await readImageDims(srcDataUrl);
        area = { x: 0, y: 0, width: dims.width, height: dims.height };
      }
      // PNG/WebP preservan transparencia → output PNG. JPG → JPG (más liviano).
      const outputMime = sourceMime === "image/png" || sourceMime === "image/webp"
        ? "image/png"
        : "image/jpeg";
      const blob = await getCroppedBlob(srcDataUrl, area, rotation, outputMime);
      if (blob.size > MAX_UPLOAD_BYTES) {
        toast(`Imagen recortada aún muy grande (${(blob.size / 1024 / 1024).toFixed(1)} MB). Probá un crop más chico.`);
        return;
      }
      const res = await uploadImage(blob, folder);
      onChange(res.url);
      toast("Imagen subida");
      closeCropper();
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : (e instanceof Error ? e.message : String(e));
      toast(`No pude subir: ${msg}`);
    } finally {
      setUploading(false);
    }
  };

  const removeImage = () => {
    onChange(null);
  };

  const wrapperStyle = {
    width: size,
    height: size,
  };

  const shapeClass = shape === "circle" ? "al-uploader-circle" : "al-uploader-square";

  return (
    <>
      <div className={"al-uploader " + shapeClass} style={wrapperStyle}>
        {value ? (
          <>
            <img src={value} alt="" className="al-uploader-img" />
            <div className="al-uploader-overlay">
              <button
                type="button"
                className="al-uploader-overlay-btn"
                onClick={openPicker}
                disabled={uploading || preparing}
                title="Cambiar"
              >
                <Icon name="upload" size={14} />
                Cambiar
              </button>
              <button
                type="button"
                className="al-uploader-overlay-btn al-uploader-overlay-btn--danger"
                onClick={removeImage}
                disabled={uploading || preparing}
                title="Quitar"
              >
                <Icon name="x" size={14} />
                Quitar
              </button>
            </div>
          </>
        ) : (
          <button
            type="button"
            className="al-uploader-placeholder"
            onClick={openPicker}
            disabled={uploading || preparing}
          >
            {preparing ? (
              <>
                <Icon name="upload" size={24} />
                <span>Cargando…</span>
              </>
            ) : (
              <>
                <Icon name="photo" size={24} />
                <span>{label}</span>
              </>
            )}
          </button>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT}
        onChange={onFile}
        style={{ display: "none" }}
      />

      {srcDataUrl && (
        <div
          className="al-uploader-modal-backdrop"
          role="dialog"
          aria-label="Editar imagen"
          onClick={(e) => {
            if (e.target === e.currentTarget && !uploading) closeCropper();
          }}
        >
          <div className="al-uploader-modal">
            <div className="al-uploader-modal-head">
              <div className="al-uploader-modal-title">Editar imagen</div>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={closeCropper}
                disabled={uploading}
                title="Cancelar"
              >
                <Icon name="x" size={14} />
              </button>
            </div>
            <div className="al-uploader-modal-cropper" onDoubleClick={resetView}>
              <Cropper
                image={srcDataUrl}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={aspect}
                cropShape={shape === "circle" ? "round" : "rect"}
                showGrid={false}
                // `contain` arranca con la imagen ENTERA visible dentro del
                // marco de recorte. Si la foto es vertical y el aspect es 1:1
                // verán bandas a los lados — pero al menos toda la imagen
                // está visible para decidir qué dejar (o cambiar a "Foto
                // completa" abajo si no quieren recortar nada).
                objectFit="contain"
                minZoom={0.5}
                maxZoom={5}
                zoomSpeed={0.6}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onRotationChange={setRotation}
                onCropComplete={onCropComplete}
                restrictPosition={false}
              />
            </div>

            {/* Hint clave: el problema reportado era "ya queda cortada".
                En realidad la imagen está ENTERA pero el marco confunde —
                este texto deja explícito qué área se va a guardar. */}
            <div className="al-uploader-hint">
              <span className="al-uploader-hint-dot" /> Solo el área más clara se guarda.
              ¿La foto se ve recortada? Probá <b>"Foto completa"</b> abajo.
            </div>

            {/* Selector de proporción — la clave del fix. Con "Foto completa"
                el aspect se ajusta al natural de la imagen → cero recorte. */}
            <div className="al-uploader-aspects">
              {ASPECT_PRESETS.map((p) => {
                const isActive =
                  p.ratio === "natural"
                    ? naturalDims != null
                      && Math.abs(aspect - naturalDims.width / naturalDims.height) < 0.01
                    : Math.abs(aspect - (p.ratio as number)) < 0.01;
                return (
                  <button
                    key={p.key}
                    type="button"
                    className={"al-uploader-aspect" + (isActive ? " is-active" : "")}
                    onClick={() => applyAspectPreset(p.ratio)}
                    disabled={uploading || (p.ratio === "natural" && !naturalDims)}
                    title={p.ratio === "natural"
                      ? "Usa la proporción real de tu foto — NO recorta nada"
                      : `Recortar a ${p.label}`}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>

            {/* Toolbar de edición — herramientas reales para no-técnicos */}
            <div className="al-uploader-modal-tools">
              <button
                type="button"
                className="al-uploader-tool"
                onClick={rotateLeft}
                disabled={uploading}
                title="Rotar 90° a la izquierda"
              >
                <span style={{ display: "inline-block", transform: "scaleX(-1)" }}>↻</span>
                <span className="tiny">Rotar</span>
              </button>
              <button
                type="button"
                className="al-uploader-tool"
                onClick={rotateRight}
                disabled={uploading}
                title="Rotar 90° a la derecha"
              >
                ↻
                <span className="tiny">Rotar</span>
              </button>
              <div className="al-uploader-tool-sep" />
              <button
                type="button"
                className="al-uploader-tool"
                onClick={resetView}
                disabled={uploading}
                title="Centrar y poner zoom 100% (doble clic en la imagen también funciona)"
              >
                ⛶
                <span className="tiny">Centrar</span>
              </button>
            </div>

            {/* Slider de zoom con indicador % — más claro que un rango mudo */}
            <div className="al-uploader-modal-zoom">
              <button
                type="button"
                className="al-uploader-zoom-btn"
                onClick={() => setZoom((z) => Math.max(0.5, z - 0.1))}
                disabled={uploading}
                title="Alejar"
                aria-label="Alejar"
              >
                <Icon name="x" size={10} />
              </button>
              <input
                type="range"
                min={0.5}
                max={5}
                step={0.05}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                disabled={uploading}
                aria-label="Zoom"
                className="al-uploader-zoom-range"
              />
              <button
                type="button"
                className="al-uploader-zoom-btn"
                onClick={() => setZoom((z) => Math.min(5, z + 0.1))}
                disabled={uploading}
                title="Acercar"
                aria-label="Acercar"
              >
                <Icon name="plus" size={10} />
              </button>
              <span className="al-uploader-zoom-val">{Math.round(zoom * 100)}%</span>
            </div>

            <div className="al-uploader-modal-foot">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={closeCropper}
                disabled={uploading}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={applyCrop}
                disabled={uploading}
              >
                {uploading ? "Subiendo…" : (
                  <>
                    <Icon name="check" size={14} /> Aplicar
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ---- Canvas / image helpers ------------------------------------------------

function readAsDataURL(f: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = () => reject(new Error("FileReader falló"));
    r.readAsDataURL(f);
  });
}

/** Lee width/height naturales de una imagen (dataURL o URL). */
async function readImageDims(src: string): Promise<{ width: number; height: number }> {
  const img = await loadImage(src);
  return { width: img.naturalWidth, height: img.naturalHeight };
}

/** Si el image source supera maxSide en el lado mayor, lo reduce a esa
 *  dimensión preservando proporción. Devuelve dataURL del mismo tipo del
 *  source (PNG/WebP → PNG para preservar transparencia; resto → JPEG).
 *  Si no hace falta reducir, devuelve el dataURL original sin tocar. */
async function downscaleDataUrl(dataUrl: string, maxSide: number, sourceMime: string): Promise<string> {
  const img = await loadImage(dataUrl);
  const longest = Math.max(img.naturalWidth, img.naturalHeight);
  if (longest <= maxSide) return dataUrl;
  const scale = maxSide / longest;
  const w = Math.round(img.naturalWidth * scale);
  const h = Math.round(img.naturalHeight * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return dataUrl;
  ctx.drawImage(img, 0, 0, w, h);
  // PNG conserva alpha; PNG/WebP source → PNG output. JPEG resto.
  const useTransparent = sourceMime === "image/png" || sourceMime === "image/webp";
  return useTransparent
    ? canvas.toDataURL("image/png")
    : canvas.toDataURL("image/jpeg", 0.9);
}

/** Extrae el blob recortado del dataURL aplicando rotación. Si la rotación
 *  es ≠0 pintamos primero la imagen rotada sobre un canvas intermedio
 *  (para que el área de crop lea desde la imagen ya orientada).
 *
 *  `outputMime`: "image/png" preserva transparencia (logos), "image/jpeg"
 *  comprime más (fotos). Antes era hard-coded a JPEG → logos PNG transparentes
 *  perdían el alpha y salían cuadrados con fondo negro. */
async function getCroppedBlob(srcDataUrl: string, area: Area, rotation: number, outputMime: "image/png" | "image/jpeg" = "image/jpeg"): Promise<Blob> {
  const img = await loadImage(srcDataUrl);

  // Si hay rotación, primero pintamos la imagen rotada sobre un canvas
  // intermedio del tamaño correspondiente. Esto evita math trigonométrica
  // sobre el área de crop.
  const source = rotation
    ? await rotateImage(img, rotation)
    : { canvas: imageToCanvas(img), width: img.naturalWidth, height: img.naturalHeight };

  // Limitamos el lado mayor a MAX_OUTPUT_SIDE para no subir originales
  // de 4000x4000 del celular (suficiente para fotos de perfil / logos).
  const scale = Math.min(1, MAX_OUTPUT_SIDE / Math.max(area.width, area.height));
  const outW = Math.max(1, Math.round(area.width * scale));
  const outH = Math.max(1, Math.round(area.height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D context no disponible");
  ctx.drawImage(
    source.canvas,
    area.x, area.y, area.width, area.height,
    0, 0, outW, outH,
  );

  return await new Promise<Blob>((resolve, reject) => {
    // PNG: sin calidad (lossless). JPEG: 0.9 (alta).
    if (outputMime === "image/png") {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error("toBlob devolvió null"))),
        "image/png",
      );
    } else {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error("toBlob devolvió null"))),
        "image/jpeg",
        0.9,
      );
    }
  });
}

/** Crea un canvas con la imagen pintada al natural. */
function imageToCanvas(img: HTMLImageElement): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = img.naturalWidth;
  c.height = img.naturalHeight;
  const ctx = c.getContext("2d");
  if (ctx) ctx.drawImage(img, 0, 0);
  return c;
}

/** Pinta la imagen rotada `rotation` grados sobre un canvas — devuelve el
 *  canvas + las nuevas dimensiones (que difieren cuando rotación es ±90°). */
async function rotateImage(
  img: HTMLImageElement,
  rotation: number,
): Promise<{ canvas: HTMLCanvasElement; width: number; height: number }> {
  const rad = (rotation * Math.PI) / 180;
  const sin = Math.abs(Math.sin(rad));
  const cos = Math.abs(Math.cos(rad));
  const w = Math.round(img.naturalWidth * cos + img.naturalHeight * sin);
  const h = Math.round(img.naturalWidth * sin + img.naturalHeight * cos);

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return { canvas, width: w, height: h };
  ctx.translate(w / 2, h / 2);
  ctx.rotate(rad);
  ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
  return { canvas, width: w, height: h };
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("No pude cargar la imagen para recortar"));
    img.src = src;
  });
}
